/* Core & libraries */
import React, { useRef } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage}  from "react-intl";
import { Link } from "react-router-dom";

/* Helpers, constants, custom methods, internal libraries */
import { ReactComponent as ArrowLeft } from "../../../resources/images/arrow-left-404-page.svg";
import { ReactComponent as ArrowRight } from "../../../resources/images/arrow-right.svg";
import { ReactComponent as GoldIcon } from "../../../resources/images/aadvantagePassIcons/gold-icon.svg";
import { ReactComponent as CheckIcon } from "../../../resources/images/check-circle.svg";
import { ReactComponent as Briefcase } from "../../../resources/images/briefcase.svg";
import { ReactComponent as UserRight } from "../../../resources/images/aadvantagePassIcons/user-right-02.svg";
import { ReactComponent as UsersUp } from "../../../resources/images/aadvantagePassIcons/users-up.svg";
import { ReactComponent as Star } from "../../../resources/images/star-03.svg";
import { ReactComponent as Plane } from "../../../resources/images/plane.svg";

/* Components */

/* Styles */
import styles from "./styles.js"
import { colors } from "constants/colors";

const cards = [
  { 
    title: "AAdvantage Gold Pass™", 
    mainIcon: <GoldIcon />,
    colorPalette: colors.yellow600,
    price: "$10,000",
    basicAdvantages: [
      {
        icon: <CheckIcon />,
        description: "AAdvantage® Gold status for a year"
      },
      {
        icon: <CheckIcon />,
        description: "100,000 AAdvantage® bonus miles"
      },
       {
        icon: <CheckIcon />,
        description: "15,000 loyalty points"
      }
    ],
    statusBenefits: [
      {
        icon: <Star />,
        description: <>Earn <strong>40% more</strong> points and miles on eligible flights</>
      },
      {
        icon: <UserRight />,
        description: <>Priority check-in security and <strong>group 4 boarding</strong></>
      },
      {
        icon: <UsersUp />,
        description: <>Complimentary upgrades (if eligible seats are available) up to <strong>24 hours before departure</strong></>
      },
      {
        icon: <Briefcase />,
        description: <><strong>First</strong> checked bag free</>
      },
      {
        icon: <CheckIcon />,
        description: "Complimentary preferred seats at booking: Main cabin seats at check in"
      },
    ]
  },
  { 
    title: "AAdvantage Gold Pass™", 
    mainIcon: <GoldIcon />,
    colorPalette: colors.greyNeutral,
    price: "$10,000",
    basicAdvantages: [
      {
        icon: <CheckIcon />,
        description: "AAdvantage® Gold status for a year"
      },
      {
        icon: <CheckIcon />,
        description: "100,000 AAdvantage® bonus miles"
      },
       {
        icon: <CheckIcon />,
        description: "15,000 loyalty points"
      }
    ],
    statusBenefits: [
      {
        icon: <Star />,
        description: <>Earn <strong>40% more</strong> points and miles on eligible flights</>
      },
      {
        icon: <UserRight />,
        description: <>Priority check-in security and <strong>group 4 boarding</strong></>
      },
      {
        icon: <UsersUp />,
        description: <>Complimentary upgrades (if eligible seats are available) up to <strong>24 hours before departure</strong></>
      },
      {
        icon: <Briefcase />,
        description: <><strong>First</strong> checked bag free</>
      },
      {
        icon: <CheckIcon />,
        description: "Complimentary preferred seats at booking: Main cabin seats at check in"
      },
    ]
  },
  { 
    title: "AAdvantage Gold Pass™", 
    mainIcon: <GoldIcon />,
    colorPalette: colors.blue900,
    price: "$10,000",
    basicAdvantages: [
      {
        icon: <CheckIcon />,
        description: "AAdvantage® Gold status for a year"
      },
      {
        icon: <CheckIcon />,
        description: "100,000 AAdvantage® bonus miles"
      },
       {
        icon: <CheckIcon />,
        description: "15,000 loyalty points"
      }
    ],
    statusBenefits: [
      {
        icon: <Star />,
        description: <>Earn <strong>40% more</strong> points and miles on eligible flights</>
      },
      {
        icon: <UserRight />,
        description: <>Priority check-in security and <strong>group 4 boarding</strong></>
      },
      {
        icon: <UsersUp />,
        description: <>Complimentary upgrades (if eligible seats are available) up to <strong>24 hours before departure</strong></>
      },
      {
        icon: <Briefcase />,
        description: <><strong>First</strong> checked bag free</>
      },
      {
        icon: <CheckIcon />,
        description: "Complimentary preferred seats at booking: Main cabin seats at check in"
      },
    ]
  },
];

const PackagesCarousel = () => {
  const theme = useTheme();
  const classes = styles({ theme });

    const scrollRef = useRef(null);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const scrollAmount = scrollRef.current.offsetWidth * 0.8;
    scrollRef.current.scrollBy({
      left: dir === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <div className={classes.container}>
        <div className={classes.header}>
          <div className={classes.textBlock}>
            <h2 className={classes.title}>Compare packages</h2>
            <p className={classes.subtitle}>
              Once assigned, registered travelers can start enjoying exclusive AAdvantage® status benefits right away.
            </p>
          </div>

          <div className={classes.buttons}>
            <button onClick={() => scroll("left")} className={classes.navButtonLeft}>
              <ArrowLeft />
            </button>
            <button onClick={() => scroll("right")} className={classes.navButtonRight}>
              <ArrowRight />
            </button>
          </div>
        </div>

        <div className={classes.carouselWrapper}>
          <div ref={scrollRef} className={classes.scrollArea}>
            {cards.map((card, idx) => {
              const classes = styles({ colorPalette: card.colorPalette });
              return (
                <div key={idx} className={classes.card}>
                  <div className={classes.cardHeader} />
                
                  <div className={classes.cardBody}>
                    <div className={classes.cardTitleRow}>
                      <h3 className={classes.cardTitle}>{card.title}</h3>
                      <div className={classes.cardMainIcon}>{card.mainIcon}</div>
                    </div>

                    <ul className={classes.cardList}>
                      {card.basicAdvantages.map((item, i) => (
                        <li key={i} className={classes.cardListItem}>
                          <span className={classes.icon}>{item.icon}</span>
                          <span>{item.description}</span>
                        </li>
                      ))}
                    </ul>

                    <div className={classes.dividerContainer}>
                      <hr className={classes.divider} />
                       <p className={classes.statusTitle}>
                        <Plane /> Status benefits include
                      </p>
                      <hr className={classes.divider} />  
                    </div>

                    <div className={classes.statusSection}>
                      <ul className={classes.cardList}>
                        {card.statusBenefits.map((item, i) => (
                          <li key={i} className={classes.cardListItem}>
                            <span className={classes.icon}>{item.icon}</span>
                            <span>{item.description}</span>
                          </li>
                        ))}
                      </ul>
                      <span className={classes.more}><FormattedMessage id="aadvantage.pass.more"/></span>
                    </div>

                    <hr className={classes.divider} />

                    <div className={classes.cardFooter}>
                      <p className={classes.price}>{card.price}</p>
                      <Link to={""} target="_blank" className={classes.explorePackage}>
                        <FormattedMessage id="aadvantage.pass.explore.package"/>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
  )
};

export default PackagesCarousel;
